import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Framework {
  id: string
  name: string
  description: string
  status: string
  compliance_score: number
  last_audit: string
  next_audit: string
  requirements_met: number
  requirements_total: number
  controls: { name: string; status: string }[]
}

interface AuditLog {
  id: string
  timestamp: string
  user: string
  action: string
  resource: string
  status: string
  ip_address: string
  details: string
}

interface Finding {
  id: string
  framework: string
  severity: string
  title: string
  description: string
  status: string
  due_date: string
  assigned_to: string
}

export default function AuditCompliancePage() {
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [findings, setFindings] = useState<Finding[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [fwRes, logRes, findRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/audit-compliance/frameworks'),
      fetch(API_BASE + '/audit-compliance/audit-logs'),
      fetch(API_BASE + '/audit-compliance/findings'),
      fetch(API_BASE + '/audit-compliance/statistics')
    ])
    if (fwRes.ok) setFrameworks((await fwRes.json()).frameworks || [])
    if (logRes.ok) setAuditLogs((await logRes.json()).audit_logs || [])
    if (findRes.ok) setFindings((await findRes.json()).findings || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': case 'passed': return 'bg-green-100 text-green-800'
      case 'in_progress': case 'warning': return 'bg-yellow-100 text-yellow-800'
      case 'non_compliant': case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Audit & Compliance Dashboard</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Frameworks</div>
              <div className="text-2xl font-bold">{statistics.total_frameworks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Compliant</div>
              <div className="text-2xl font-bold text-green-600">{statistics.compliant_frameworks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Score</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.average_compliance_score}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Findings</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.open_findings}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Critical</div>
              <div className="text-2xl font-bold text-red-600">{statistics.critical_findings}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Next Audit</div>
              <div className="text-2xl font-bold">{statistics.next_audit_days}d</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-6">
          {frameworks.map(fw => (
            <div key={fw.id} className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg" onClick={() => setSelectedFramework(fw)}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{fw.name}</h3>
                  <p className="text-sm text-gray-500">{fw.description}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${getStatusColor(fw.status)}`}>{fw.status}</span>
              </div>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Compliance Score</span>
                  <span className="font-semibold">{fw.compliance_score}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${fw.compliance_score >= 90 ? 'bg-green-500' : fw.compliance_score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${fw.compliance_score}%` }}></div>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                <div>Requirements: {fw.requirements_met}/{fw.requirements_total}</div>
                <div>Next Audit: {new Date(fw.next_audit).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Audit Log</h2></div>
            <div className="divide-y max-h-96 overflow-y-auto">
              {auditLogs.map(log => (
                <div key={log.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{log.action.replace(/_/g, ' ')}</div>
                      <div className="text-sm text-gray-500">{log.resource}</div>
                      <div className="text-xs text-gray-400 mt-1">{log.details}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm">{log.user}</div>
                      <div className="text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Open Findings</h3>
              <div className="space-y-3">
                {findings.filter(f => f.status !== 'closed').map(finding => (
                  <div key={finding.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{finding.title}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(finding.severity)}`}>{finding.severity}</span>
                    </div>
                    <div className="text-xs text-gray-500">{finding.framework}</div>
                    <div className="text-xs text-gray-400 mt-1">Due: {new Date(finding.due_date).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>

            {selectedFramework && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedFramework.name} Controls</h3>
                <div className="space-y-2">
                  {selectedFramework.controls.map((ctrl, i) => (
                    <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="text-sm">{ctrl.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(ctrl.status)}`}>{ctrl.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
